import { useEffect, useRef, useState } from "react";

// The instant greeting (greeting.ts) used to appear fully formed on the first
// frame, which read as "already there" rather than Courtney talking. It now
// plays like her other replies: typing dots for a beat, then the words stream
// in. About 1 s total; reduced-motion users get it instantly.
export const GREETING_DOTS_MS = 450;
export const GREETING_WORD_MS = 60;

export type GreetingFrame = { phase: "dots" | "typing" | "done"; shown: string };

const DONE: GreetingFrame = { phase: "done", shown: "" };

/** What the greeting looks like `elapsedMs` after the chat opened. Pure, for tests. */
export function typedGreetingFrame(text: string, elapsedMs: number): GreetingFrame {
  if (elapsedMs < GREETING_DOTS_MS) return { phase: "dots", shown: "" };
  const words = text.split(" ");
  const count = Math.floor((elapsedMs - GREETING_DOTS_MS) / GREETING_WORD_MS) + 1;
  if (count >= words.length) return { phase: "done", shown: text };
  return { phase: "typing", shown: words.slice(0, count).join(" ") };
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Plays the greeting once, starting the first time `text` is non-null (a chat
 * that opened fresh). It keeps playing if `text` later goes null because
 * Courtney's own greeting arrived, so the hand-off never jumps to full text.
 */
export function useTypedGreeting(text: string | null): GreetingFrame {
  const textRef = useRef<string | null>(null);
  const startRef = useRef(0);
  const skipRef = useRef<boolean | null>(null);
  if (skipRef.current === null) skipRef.current = prefersReducedMotion();
  if (text && textRef.current === null && !skipRef.current) {
    textRef.current = text;
    startRef.current = Date.now();
  }
  const [, tick] = useState(0);
  const locked = textRef.current;
  const frame = locked ? typedGreetingFrame(locked, Date.now() - startRef.current) : DONE;
  useEffect(() => {
    if (!locked || frame.phase === "done") return;
    const id = setTimeout(() => tick((t) => t + 1), 30);
    return () => clearTimeout(id);
  });
  return frame;
}
